import { observer } from 'mobx-react';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { Box } from '@mui/material';
import convertSchemaToColumns from './lib/convertSchemaToColumns';
import { useLocalization } from '../../providers/LocalizationProvider';
import { useStore } from '../../providers/MobxProvider';
import LoggerUtil from '../../helpers/LoggerUtil';
import { useSnackbar } from '../../providers/SnackbarProvider';
import SnackbarApiErrorMessage from '../UI/SnackbarApiErrorMessage';

export function buildSortParam({
  sortingState = [],
  sortBy = '',
  sortOrder = '',
}) {
  if (Array.isArray(sortingState) && sortingState.length > 0) {
    return sortingState
      .map(
        ({ id, desc }) => `${id.replace('[.]', '.')}:${desc ? 'DESC' : 'ASC'}`,
      )
      .join(',');
  }

  const defaultSort = [sortBy, sortOrder].join(':');
  return defaultSort === ':' ? '' : defaultSort;
}

const FormListAdvanced = forwardRef(
  (
    {
      columnPrimaryId,
      schema,
      dataProvider,
      initialState,
      listApiUrl,
      accessorFunctions = {},
      onRowCountChange = () => undefined,
      accessorFunctionsAdditional = {},
      children = null,
      onApiResponse = () => undefined,
      stickyColumn = false,
      storeName,
      responseFormatter = null,
      sortOrder = '',
      sortBy = '',
      rowsPerPageOptions = [5, 10, 15, 20, 25, 30, 50],
    },
    ref,
  ) => {
    const { formatMessage } = useLocalization();
    const store = useStore();
    const snackbar = useSnackbar();

    const {
      paginationState,
      filters = [],
      sortingState = [],
    } = store.domain.list.get(storeName);

    const { get } = dataProvider;

    // data and fetching state
    const [tableData, setTableData] = useState([]);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);

    // table state
    const [rowCount, setRowCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const currentAPIUrl = useRef(new URLSearchParams());

    const columns = useMemo(() =>
      convertSchemaToColumns(
        schema,
        formatMessage,
        accessorFunctions,
        accessorFunctionsAdditional,
        stickyColumn,
      ),
    );

    async function fetchData() {
      if (!tableData.length) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }

      const apiUrl = new URL(listApiUrl);

      apiUrl.searchParams.set('page', `${paginationState.pageIndex + 1 || 1}`);
      apiUrl.searchParams.set('limit', `${paginationState?.pageSize || 10}`);

      if (Array.isArray(filters) && filters.length > 0) {
        filters.every(filter => {
          const newFilter = filter;

          // filter?.value?.operator to be used for DynamicDatePicker
          apiUrl.searchParams.set(
            `${newFilter?.searchAs || newFilter?.field}`,
            `$${
              newFilter?.value?.operator === 'eqNull'
                ? 'eq'
                : newFilter?.value?.operator || newFilter?.operator
            }:${
              newFilter?.value?.operator === 'eqNull'
                ? null
                : newFilter?.value?.value || newFilter?.value
            }`,
          );
          return true;
        });
        currentAPIUrl.current = apiUrl.searchParams;
      }

      const sort = buildSortParam({ sortingState, sortBy, sortOrder });
      if (sort) {
        apiUrl.searchParams.set('sortBy', sort);
      }

      try {
        const apiResponse = await get(apiUrl.href);
        onApiResponse(apiResponse);
        if (apiResponse?.ok) {
          const response = apiResponse.data;
          let tableDataResponse = response.data;
          if (responseFormatter) {
            tableDataResponse = responseFormatter(tableDataResponse);
          }
          setTableData(tableDataResponse);
          // TODO: Remove this hack once API has a standard way of sending total
          const tempRowCount =
            response?.total || response?.totalItems || response?.data.length;
          setRowCount(tempRowCount);
          setPageSize(response?.limit || 10);
        } else {
          snackbar.showMessage({
            type: 'error',
            data: <SnackbarApiErrorMessage errors={apiResponse?.errors} />,
          });
        }
      } catch (error) {
        setIsError(true);
        LoggerUtil.log('List API Error', error);
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    }

    useEffect(() => {
      fetchData();
    }, [
      JSON.stringify(paginationState),
      JSON.stringify(filters),
      JSON.stringify(sortingState),
    ]);

    useEffect(() => {
      onRowCountChange(rowCount);
    }, [rowCount]);

    const table = useMaterialReactTable({
      data: tableData,
      autoResetPageIndex: false,
      localization: store.domain.locale.getForMuiTable(),
      columns,
      getRowId: row => row[columnPrimaryId],
      rowCount,
      pageSize,
      manualFiltering: true,
      enableTopToolbar: false,
      enableBottomToolbar: true,
      manualPagination: true,
      manualSorting: true,
      enableColumnFilters: false,
      enableSorting: true,
      enableFilters: false,
      initialState,
      enableColumnActions: false,
      muiTablePaperProps: { elevation: 0 },
      muiPaginationProps: {
        ...(rowsPerPageOptions !== null && { rowsPerPageOptions }),
        disabled: isLoading || isRefetching,
      },
      state: {
        globalFilter: '',
        isLoading,
        showAlertBanner: isError,
        showProgressBars: isRefetching,
        sorting: sortingState,
        pagination: paginationState,
      },
      muiBottomToolbarProps: {
        sx: {
          minHeight: '2.5em',
        },
      },
      muiToolbarAlertBannerProps: isError
        ? {
            color: 'error',
            children: formatMessage({
              id: 'ERROR_LOADING_DATA',
              defaultMessage: 'Error loading data',
            }),
          }
        : undefined,
      onPaginationChange: updater => {
        // TODO: remove after version upgrade
        const newPagination =
          typeof updater === 'function' ? updater(paginationState) : updater;
        store.domain.list.set(storeName, {
          paginationState: newPagination,
          sortingState,
          filters,
        });
      },
      onSortingChange: updater => {
        // TODO: remove after version upgrade
        const newSorting =
          typeof updater === 'function' ? updater(sortingState) : updater;

        store.domain.list.set(storeName, {
          paginationState: { pageIndex: 0, pageSize },
          sortingState: newSorting,
          filters,
        });
      },
    });

    useImperativeHandle(ref, () => ({
      getFilter: () => currentAPIUrl.current,
      getVisibleColumns: () => table.getVisibleLeafColumns(),
      reset: table.resetColumnVisibility,
      setCompact: () => table.setDensity('compact'),
    }));

    return (
      <Box ref={ref}>
        <MaterialReactTable table={table} />
        {children}
      </Box>
    );
  },
);

export default observer(FormListAdvanced);
